import { ChartColorService } from "src/app/services/chart-color.service";
import { IQueryContext } from "src/app/services/query-context/models/IQueryContext";

// Default color palette for ECharts
const ECHARTS_DEFAULT_PALETTE_BASE = [
    '#E6194B',
    '#3CB44B',
    '#FFE119',
    '#4363D8',
    '#F58231',
    '#911EB4',
    '#42D4F4',
    '#F032E6',
    '#BFEF45',
    '#FABED4',
    '#469990',
    '#DCBEFF',
    '#9A6324',
    '#FFFAC8',
    '#800000',
    '#808000',
    '#000075',
    '#AAFFC3',
    '#FFD8B1',
    '#A9A9A9',
    // '#FFFFFF',
    '#000000',

    // '#5470c6',
    // '#91cc75',
    // '#fac858',
    // '#ee6666',

    // '#73c0de',
    // '#3ba272',
    // '#fc8452',
    // '#9a60b4',

    // '#ea7ccc',
    // '#c23531',
    // '#2f4554',
    // '#61a0a8',
    // '#d48265',

    // '#91c7ae',
    // '#749f83',
    // '#ca8622',
    // '#bda29a',
    // '#6e7074',

    // '#546570',
    // '#c4ccd3',
];

const randomArrs = [...Array(100).keys()].map((i) => {
    return structuredClone(ECHARTS_DEFAULT_PALETTE_BASE.sort(() => Math.random()));
});

// flatten randomArrs
const ECHARTS_DEFAULT_PALETTE = randomArrs.reduce(
    (acc, val) => acc.concat(val),
    []
);

// Map of hardcoded split values to specific colors
const HARDCODED_SPLIT_VALUE_TO_COLOR_MAP: { [k: string]: string } = {
    // '': '#5470c6',
    // '1': '#5470c6',
    // '2': '#91cc75',
    // '3': '#fac858',
    // MOPA: 'rgb(97, 160, 168)',
    // 'MOPA, Amazon Appstore': 'rgb(97, 160, 168)',
    // 'MOPA, Google Play': 'hsl(60, 100%, 50%)',
    // 'MOPA, Microsoft Store': 'rgb(97, 160, 168)',
    // 'MOPA, iTunes': 'rgb(97, 160, 168)',
    // 'MOPA, iTunes macOS': 'rgb(97, 160, 168)',
    // Bladebound: 'rgb(250, 200, 88)',
    // 'Bladebound, Google Play': 'rgb(150, 200, 88)',
    // 'Bladebound, iTunes': 'rgb(50, 200, 88)',
};

/**
 * Returns the color for a series based on its split value.
 *
 * @param splitValue - The split value of the series.
 *
 * @returns An object with the color property if a color is found, otherwise an empty object.
 */
export function getSeriesColorBySplitValue(
    splitValue: string
): { color: string } | {} {
    // throw error if splitValue is not a string
    if (typeof splitValue !== 'string') {
        throw new Error(
            `getSeriesColorBySplitValue: splitValue must be a string, received ${typeof splitValue}`
        );
    }
    // Check if there's a hardcoded color for this split value
    const color =
        HARDCODED_SPLIT_VALUE_TO_COLOR_MAP[splitValue] ??
        // ChartColorService.getItem(splitValue);
        // If not, generate a color based on the hash of the split value
        stringHashToPallete(splitValue, ECHARTS_DEFAULT_PALETTE);
    return color ? { color } : {};
}

/**
 * Hashes a string to a color from a given palette.
 *
 * @param s - The string to hash.
 * @param palette - The color palette to use.
 *
 * @returns An object with the color property set to the hashed color.
 */
function stringHashToPallete(s: string, palette: string[]) {
    // Hash the string to a number
    const hash = s.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    // console
    // Use the absolute value of the hash modulo the length of the palette to get an index
    const index = Math.abs(hash % palette.length);

    console.log('stringHashToPallete', s, hash, index, palette[index]);
    // Return the color at that index in the palette
    return palette[index];
}

/**
 * This function builds a default color getter for a chart.
 * It uses the provided color service and query context to determine the color for each series.
 *
 * @param {ChartColorService} colorService - The service used to get colors for the chart.
 * @param {IQueryContext} context - The context of the query, used to determine the split columns.
 *
 * @returns {Function} - A function that takes a string and returns an object with a color property.
 */
export function defaultColorGetterBuilder(colorService: ChartColorService, context: IQueryContext) {
    // get all split columns ids
    const splitColumnsIds = context.datasourceColumns
        .filter((dc) => dc.state.isSplitApplied)
        .map((dc) => dc.id);
    // create layer from spliColumns
    const layer = ChartColorService.getLayerString(splitColumnsIds);
    return (s: string) => ({
        color: colorService.getColor(layer, s),
    });
}